import React, { useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";
import { loadingCard } from "../../../helpers/loading";

interface InteractionsTableProps {
  data: any;
}

interface TypePickerProps {
  types: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

const TERTIARY_COLOR = "#da7e39";
const HEAT_RGB = "218, 126, 57"; // same as TERTIARY_COLOR
const columnWidth = 108;

// Optional column picker; remove this component and its usage to drop the feature.
const TypePicker = ({
  types,
  selected,
  onChange,
}: TypePickerProps): React.JSX.Element => {
  const toggle = (t: string) =>
    onChange(
      selected.includes(t) ? selected.filter((s) => s !== t) : [...selected, t],
    );

  return (
    <div
      className="mb-3 d-flex flex-wrap align-items-center"
      style={{ gap: "6px" }}
    >
      {types.map((t) => {
        const isOn = selected.includes(t);
        return (
          <button
            key={t}
            type="button"
            className={`btn btn-sm ${isOn ? "btn-secondary" : "btn-outline-secondary"}`}
            style={{
              borderRadius: "999px",
              padding: "0 10px",
              fontSize: "0.8rem",
            }}
            aria-pressed={isOn}
            onClick={() => toggle(t)}
          >
            {t}
          </button>
        );
      })}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 ms-1"
        style={{ color: TERTIARY_COLOR }}
        onClick={() => onChange(types)}
      >
        All
      </button>
      <button
        type="button"
        className="btn btn-link btn-sm p-0"
        style={{ color: TERTIARY_COLOR }}
        onClick={() => onChange([])}
      >
        None
      </button>
    </div>
  );
};

export const InteractionsTable = ({
  data,
}: InteractionsTableProps): React.JSX.Element | null => {
  const [targetTypes, setTargetTypes] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [tableData, setTableData] = useState<any[]>();
  const [sortDesc, setSortDesc] = useState<boolean>(true);

  useEffect(() => {
    if (data) {
      const { notes } = data;
      const noteTypesArr = notes.note_type_counts || [];
      const filteredNoteTypes = noteTypesArr?.filter(
        (t: any) => t?.type !== "system",
      );
      const noteTypeNames = filteredNoteTypes.map((n: any) => n.name);
      setTargetTypes(noteTypeNames);
      setSelectedTypes(noteTypeNames);

      const table = notes.creator_note_type_totals || [];
      // ensure totals have 0 for missing types
      const normalizedTable = table.map((t: any) => {
        const totals = {} as Record<string, number>;
        for (const nt of noteTypeNames) {
          totals[nt] = t.totals && t.totals[nt] ? t.totals[nt] : 0;
        }
        return { ...t, totals };
      });
      setTableData(normalizedTable);
    } else {
      setTableData([]);
    }
  }, [data]);

  // Same array drives the headers and the cells so they can never get out of order
  const visibleTypes = useMemo(
    () => targetTypes.filter((t) => selectedTypes.includes(t)),
    [targetTypes, selectedTypes],
  );

  const rows = useMemo(() => {
    const withTotals = (tableData || []).map((d: any) => ({
      ...d,
      total: Object.values(d.totals as Record<string, number>).reduce(
        (sum, num) => sum + num,
        0,
      ),
    }));
    return withTotals.sort((a, b) =>
      sortDesc ? b.total - a.total : a.total - b.total,
    );
  }, [tableData, sortDesc]);

  // Scale is fixed across all note types so hiding columns doesn't change cell shading
  const maxCell = useMemo(
    () =>
      Math.max(
        0,
        ...rows.flatMap((d: any) => targetTypes.map((t) => d.totals[t] || 0)),
      ),
    [rows, targetTypes],
  );

  if (!tableData) {
    return loadingCard();
  }

  const heatStyle = (value: number): React.CSSProperties => {
    if (value <= 0 || maxCell <= 0) return {};
    const intensity = value / maxCell;
    return {
      backgroundColor: `rgba(${HEAT_RGB}, ${intensity.toFixed(2)})`,
      color: intensity > 0.6 ? "#ffffff" : undefined,
    };
  };

  return (
    <>
      <h5 className="app-header-text mb-3">Interactions</h5>
      <TypePicker
        types={targetTypes}
        selected={selectedTypes}
        onChange={setSelectedTypes}
      />
      <div style={{ overflowX: "auto" }}>
        <Table
          striped
          style={{
            tableLayout: "fixed",
            width: `${columnWidth * visibleTypes.length + 270}px`,
            minWidth: "100%",
          }}
        >
          <thead>
            <tr>
              <th style={{ width: "190px", verticalAlign: "bottom" }}>
                Case Manager
              </th>
              <th
                style={{
                  width: "80px",
                  cursor: "pointer",
                  verticalAlign: "bottom",
                }}
                onClick={() => setSortDesc((s) => !s)}
                aria-sort={sortDesc ? "descending" : "ascending"}
              >
                Total {sortDesc ? "▼" : "▲"}
              </th>
              {visibleTypes.map((t: string) => (
                <th
                  key={t}
                  title={t}
                  style={{
                    width: columnWidth,
                    verticalAlign: "bottom",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((d: any) => {
              const name = d.creator_firstname + " " + d.creator_lastname;
              return (
                <tr key={d.created_by}>
                  <td>{name}</td>
                  <td>{d.total}</td>
                  {visibleTypes.map((t) => (
                    <td
                      key={t}
                      title={`${name}, ${t}: ${d.totals[t]}`}
                      style={heatStyle(d.totals[t])}
                    >
                      {d.totals[t]}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={visibleTypes.length + 2} className="text-center">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};
